import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import {
  Button,
  Field,
  Input,
  PageHeader,
  Panel,
  Stack,
  StatusBadge,
} from "@/components/ui";

import { Facts } from "../components/Text";

import { useApprovePairing, useMachines } from "./lib/queries";
import { errorText } from "./lib/present";

const CONFIRMATION_POLL_MS = 3000;

/** Approve the code a machine is showing. The typed code never reaches the URL, and this screen
 * never shows one of its own: see Convention_ConsoleNeverRedisplaysAPairingCode. */
export function ConsolePairMachine() {
  const navigate = useNavigate();
  const approve = useApprovePairing();
  const machines = useMachines();
  const [code, setCode] = useState("");
  const baseline = useRef<number | null>(null);

  const used = machines.data?.length ?? 0;
  const status = approve.data?.status;
  const awaitingConfirmation = status === "approved";

  useEffect(() => {
    if (!awaitingConfirmation) {
      baseline.current = null;
      return;
    }
    baseline.current ??= machines.data?.length ?? 0;
    const id = setInterval(() => {
      void machines.refetch();
    }, CONFIRMATION_POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingConfirmation]);

  useEffect(() => {
    if (!awaitingConfirmation || baseline.current === null) return;
    if ((machines.data?.length ?? 0) > baseline.current) {
      void navigate({ to: "/console/machines" });
    }
  }, [awaitingConfirmation, machines.data, navigate]);

  return (
    <Stack gap={6}>
      <PageHeader
        eyebrow="Machines"
        title="Pair a machine"
        actions={
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              void navigate({ to: "/console/machines" });
            }}
          >
            Machines
          </Button>
        }
      />

      {awaitingConfirmation ? (
        <Panel title="Confirm on the machine">
          <Stack gap={4}>
            <p>Go to the machine and confirm it says your account name.</p>
            <div className="cx-verbs">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  approve.reset();
                  setCode("");
                }}
              >
                Cancel
              </Button>
            </div>
          </Stack>
        </Panel>
      ) : (
        <Panel title="Code from the machine">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              approve.mutate(code.trim());
            }}
          >
            <Stack gap={4}>
              <Field label="Code">
                {({ id }) => (
                  <Input
                    id={id}
                    mono
                    autoComplete="off"
                    value={code}
                    placeholder="ABCD-EFGH"
                    onChange={(event) => {
                      setCode(event.target.value);
                    }}
                  />
                )}
              </Field>
              <div className="cx-verbs">
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  loading={approve.isPending}
                  disabled={code.trim() === ""}
                >
                  Approve
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    void navigate({ to: "/console/machines" });
                  }}
                >
                  Cancel
                </Button>
              </div>
              <div aria-live="polite">
                {approve.isError ? (
                  <StatusBadge tone="danger">{errorText(approve.error)}</StatusBadge>
                ) : null}
                {status === "expired" ? <StatusBadge tone="warning">expired</StatusBadge> : null}
                {status === "declined" ? <StatusBadge tone="danger">declined</StatusBadge> : null}
                {status === "pending" ? <StatusBadge tone="neutral">pending</StatusBadge> : null}
              </div>
            </Stack>
          </form>
        </Panel>
      )}

      <Panel title="Machines" tone="sunken">
        <Facts
          items={[
            { label: "Paired", value: String(used) },
          ]}
        />
      </Panel>
    </Stack>
  );
}
